/** Public Quick Fill write facade. Planning and transport stay independently testable. */
import type { QuickFillItem } from '../types';
import { executePreparedRevitBatches } from './quickFillApplyTransport';
import {
  collectUpdateForCell,
  collectUpdatesForRow,
  dedupeElementUpdates,
  dedupeProjectTypeUpdates,
} from './quickFillUpdatePlanner';
import type {
  BulkUpdateResult,
  CellUpdateResult,
  PreparedElementUpdate,
  PreparedProjectTypeUpdate,
  PropertyMapping,
  QuickFillBindingScope,
  QuickFillCellUpdate,
  SaveChangesOptions,
} from './quickFillRevitTypes';
import { emitPreparingProgress } from './quickFillRevitTypes';

export type {
  BulkUpdateResult,
  CellUpdateResult,
  PropertyMapping,
  QuickFillBindingScope,
  QuickFillCellUpdate,
  QuickFillSyncProgress,
  QuickFillSyncProgressPhase,
} from './quickFillRevitTypes';

async function applyCollectedUpdates(
  projectTypeUpdates: PreparedProjectTypeUpdate[],
  elementUpdates: PreparedElementUpdate[],
  skippedResults: CellUpdateResult[],
  options: SaveChangesOptions
): Promise<BulkUpdateResult> {
  const { results, modelVersion } = await executePreparedRevitBatches(
    dedupeProjectTypeUpdates(projectTypeUpdates),
    dedupeElementUpdates(elementUpdates),
    skippedResults,
    options
  );
  const successCount = results.filter((result) => result.success).length;
  return {
    totalUpdates: results.length,
    successCount,
    failureCount: results.length - successCount,
    results,
    ...(modelVersion !== undefined ? { modelVersion } : {}),
  };
}

export async function saveCellToRevit(
  familyTypeName: string,
  revitParameterName: string,
  value: string
): Promise<CellUpdateResult> {
  const { results } = await executePreparedRevitBatches([{
    FamilyType: familyTypeName,
    ParameterName: revitParameterName,
    Value: value,
    BindingScope: 'unknown',
    rowLabel: familyTypeName,
  }], []);
  return results[0] ?? {
    familyType: familyTypeName,
    parameter: revitParameterName,
    value,
    success: false,
    error: 'Quick Fill apply returned no cell result',
  };
}

export async function saveRowsToRevit(
  items: QuickFillItem[],
  properties: PropertyMapping[],
  rowIndices?: number[],
  options: SaveChangesOptions = {}
): Promise<BulkUpdateResult> {
  emitPreparingProgress(options, 'Collecting changed Quick Fill rows');
  const projectTypeUpdates: PreparedProjectTypeUpdate[] = [];
  const elementUpdates: PreparedElementUpdate[] = [];
  const skippedResults: CellUpdateResult[] = [];
  for (const index of rowIndices ?? items.map((_, itemIndex) => itemIndex)) {
    const item = items[index];
    if (!item?.Name) continue;
    const collected = collectUpdatesForRow(item, properties);
    projectTypeUpdates.push(...collected.projectTypeUpdates);
    elementUpdates.push(...collected.elementUpdates);
    skippedResults.push(...collected.skippedResults);
  }
  return applyCollectedUpdates(projectTypeUpdates, elementUpdates, skippedResults, options);
}

export async function saveChangesToRevit(
  currentItems: QuickFillItem[],
  originalItems: QuickFillItem[],
  properties: PropertyMapping[],
  options: SaveChangesOptions = {}
): Promise<BulkUpdateResult> {
  emitPreparingProgress(options, 'Comparing current values with the loaded Revit snapshot');
  const projectTypeUpdates: PreparedProjectTypeUpdate[] = [];
  const elementUpdates: PreparedElementUpdate[] = [];
  const skippedResults: CellUpdateResult[] = [];
  currentItems.forEach((item, index) => {
    if (!item?.Name) return;
    const collected = collectUpdatesForRow(item, properties, item, originalItems[index], options);
    projectTypeUpdates.push(...collected.projectTypeUpdates);
    elementUpdates.push(...collected.elementUpdates);
    skippedResults.push(...collected.skippedResults);
  });
  return applyCollectedUpdates(projectTypeUpdates, elementUpdates, skippedResults, options);
}

export async function saveRollbackToRevit(
  currentItems: QuickFillItem[],
  restoredItems: QuickFillItem[],
  properties: PropertyMapping[],
  options: SaveChangesOptions = {}
): Promise<BulkUpdateResult> {
  return saveChangesToRevit(restoredItems, currentItems, properties, {
    includeEmptyValues: true,
    onProgress: options.onProgress,
  });
}

export async function saveCellsToRevit(
  items: QuickFillItem[],
  updates: QuickFillCellUpdate[],
  options: SaveChangesOptions = {}
): Promise<BulkUpdateResult> {
  emitPreparingProgress(options, 'Collecting exact changed Quick Fill cells');
  const projectTypeUpdates: PreparedProjectTypeUpdate[] = [];
  const elementUpdates: PreparedElementUpdate[] = [];
  const skippedResults: CellUpdateResult[] = [];
  for (const update of updates) {
    if (!Number.isInteger(update.rowIndex) || update.rowIndex < 0) continue;
    const item = items[update.rowIndex];
    if (!item?.Name) continue;
    const collected = collectUpdateForCell(
      item,
      update.field,
      update.value,
      update.revitParameter,
      update.bindingScope
    );
    projectTypeUpdates.push(...collected.projectTypeUpdates);
    elementUpdates.push(...collected.elementUpdates);
    skippedResults.push(...collected.skippedResults);
  }
  return applyCollectedUpdates(projectTypeUpdates, elementUpdates, skippedResults, options);
}

export async function saveBulkEditToRevit(
  items: QuickFillItem[],
  rowIndices: number[],
  field: string,
  value: string,
  revitParameter: string,
  options: SaveChangesOptions = {},
  bindingScope?: QuickFillBindingScope
): Promise<BulkUpdateResult> {
  return saveCellsToRevit(
    items,
    rowIndices.map((rowIndex) => ({
      rowIndex,
      field,
      value,
      revitParameter,
      bindingScope,
    })),
    options
  );
}
